// Pure, DOM-free resolver that turns a task's raw `assigneeId` into a display
// identity (name + avatar + initials) by looking it up across the three pools a
// task can be assigned to: workspace members (humans), cybos, and live agents.
// Kept pure so the Tasks board / dialog render a real name+face instead of the
// truncated id, and so the lookup is unit-testable without mounting a component.
//
// Resolution order is members → cybos → agents. The three id spaces don't
// overlap in practice (a member is a userId, a cybo is its db id, an agent is an
// agentId), so order only matters for the unreachable collision case; members
// win there because a human assignee is the most common, most authoritative one.
//
// An agent row that carries a `cyboId` FOLDS to that cybo's identity (kind "cybo",
// keyed on the stable cyboId), so a cybo's many live sessions collapse to ONE
// identity instead of one-per-session — mirroring mobile's roster.agentIdToCyboId
// fold (TaskAssigneeIdentityTests). A session with NO cyboId stays its own agent
// identity keyed on the agentId.
import { getInitials } from "$lib/utils.js";
import { agentDisplayName } from "$lib/agent-display.js";
import { sessionDisplayName } from "$lib/state/session-alias.svelte.js";
import type { WorkspaceMember } from "$lib/core/types.js";
import type { Agent, Cybo } from "$lib/plugins/agents/types.js";

export type AssigneeKind = "user" | "cybo" | "agent" | "unknown";

export interface ResolvedAssignee {
  kind: AssigneeKind;
  id: string;
  name: string;
  // An image URL OR a raw avatar string (a cybo avatar may be a single emoji).
  // Pass straight to <Avatar avatar={...} /> which resolves image|emoji|initials.
  avatarUrl?: string | null;
  initials: string;
}

export interface AssigneePools {
  members: WorkspaceMember[];
  cybos: Cybo[];
  agents: Agent[];
  /** Id -> label for a person or agent that is gone (erased, removed, deleted), from the relay. */
  gone?: Record<string, string>;
}

// A short, human-scannable label for an id that resolves to no name (an unknown
// assignee/creator, or a cybo/agent not yet in the roster). NEVER the full raw
// UUID — a cybo id is `cybo_<uuid>`, a user id `u_<uuid>`; slicing keeps the
// prefix + a few chars so it reads as a stub, not a wall of hex. Matches the
// same fallback in tasks/detail.ts.
function shortIdLabel(id: string): string {
  return id.slice(0, 8);
}

// Per-pool identity builders — one source of truth shared by BOTH the linear scan
// and the prebuilt index, so an id resolves to the exact same identity either way.
function memberAssignee(member: WorkspaceMember): ResolvedAssignee {
  const name = member.name ?? member.email;
  return {
    kind: "user",
    id: member.userId,
    name,
    avatarUrl: member.imageUrl ?? member.image ?? null,
    initials: getInitials(name),
  };
}
function cyboAssignee(cybo: Cybo): ResolvedAssignee {
  return {
    kind: "cybo",
    id: cybo.id,
    name: cybo.name,
    avatarUrl: cybo.avatar ?? null,
    initials: getInitials(cybo.name),
  };
}
function agentAssignee(agent: Agent): ResolvedAssignee {
  // Use the shared agent display resolver (cyboName -> provider label -> id
  // prefix) rather than a local cyboName fallback chain — see agent-display.ts
  // and its single-resolver guard test.
  const name = agentDisplayName(agent);
  // Fold a live session onto its stable cybo identity when the row carries a
  // cyboId — mirrors mobile's roster.agentIdToCyboId fold. All of a cybo's sessions
  // then resolve to ONE identity keyed on the cyboId, so the assignee filter /
  // grouping shows the cybo once, not once per session. A session with NO cyboId
  // (a bare/ephemeral agent) keeps its own agent identity keyed on the agentId.
  if (agent.cyboId) {
    return {
      kind: "cybo",
      id: agent.cyboId,
      name,
      avatarUrl: agent.cyboAvatar ?? null,
      initials: getInitials(name),
    };
  }
  // A bare session is the viewer's own row: their alias names it, as in the sidebar.
  const label = sessionDisplayName(agent, undefined, name);
  return {
    kind: "agent",
    id: agent.agentId,
    name: label,
    avatarUrl: agent.cyboAvatar ?? null,
    initials: getInitials(label),
  };
}
/** The live sessions an assignee picker offers under "Sessions": never one whose agent is already
 *  listed under "Agents" (its session uuid would show the same name twice), one per agent. */
export function pickerSessions(pools: AssigneePools): Agent[] {
  const seen = new Set(pools.cybos.map((c) => c.id));
  return pools.agents.filter((a) => {
    if (!a.cyboId) return true;
    if (seen.has(a.cyboId)) return false;
    seen.add(a.cyboId);
    return true;
  });
}

function unknownAssignee(id: string, gone?: Record<string, string>): ResolvedAssignee {
  // An id in NO pool is an identity that no longer exists — a deleted cybo, or a
  // session that ended and left the roster. It used to render as the raw id
  // prefix ("a4e9d6d4"), which tells the reader nothing and looks like a bug even
  // though the assignment is real data. Name the STATE instead; the id stays on
  // the object for anyone who needs to trace it.
  return {
    kind: "unknown",
    id,
    name: gone?.[id] ?? "Removed agent", // lesson 16: the relay's label when it knows who it was
    avatarUrl: null,
    initials: getInitials(shortIdLabel(id)),
  };
}

// Prebuild an id -> identity index for the whole pool ONCE, so a board of N cards
// resolves its assignees in N O(1) lookups instead of N × O(pool) scans. Insertion
// order encodes the members → cybos → agents precedence: a member id already in the
// map is never overwritten by a colliding cybo/agent id (matches the scan order).
export function buildAssigneeIndex(
  pools: AssigneePools | null | undefined,
): Map<string, ResolvedAssignee> {
  const index = new Map<string, ResolvedAssignee>();
  if (!pools) return index;
  for (const m of pools.members ?? [])
    if (m.userId && !index.has(m.userId)) index.set(m.userId, memberAssignee(m));
  for (const c of pools.cybos ?? []) if (c.id && !index.has(c.id)) index.set(c.id, cyboAssignee(c));
  for (const a of pools.agents ?? []) {
    // A session is reachable by BOTH its raw agentId (a task deliberately assigned
    // to that session) and — once folded — its cyboId (the collapsed cybo identity).
    // pools.cybos ran first, so a cyboId already claimed keeps the authoritative
    // cybo entry; the FIRST session of a cybo wins its cyboId key, later sessions
    // skip it. Both keys point at the same folded identity, in lockstep with the
    // linear scan below.
    const resolved = agentAssignee(a);
    if (a.agentId && !index.has(a.agentId)) index.set(a.agentId, resolved);
    if (a.cyboId && !index.has(a.cyboId)) index.set(a.cyboId, resolved);
  }
  return index;
}

// Resolve `id` to a display identity, or null when there is no assignee.
// An id that matches no pool resolves to kind "unknown" with a SHORT label as its
// name (never the raw UUID, never null — null is reserved for "no one is
// assigned"). Pass a prebuilt `index` (buildAssigneeIndex) for an O(1) lookup;
// without one it falls back to the linear members → cybos → agents scan.
export function resolveAssignee(
  id: string | null,
  pools: AssigneePools,
  index?: Map<string, ResolvedAssignee>,
): ResolvedAssignee | null {
  // Guard `id` (no assignee) and a null/partial `pools` (the rune-backed pools can
  // be transiently undefined during a workspace switch). A missing pool is read as
  // empty so the lookup falls through to "unknown" instead of throwing.
  if (!id || !pools) return null;

  // Fast path: a prebuilt index. An id absent from it is an "unknown" assignee (the
  // same fallback as the scan), NOT "no one assigned" (null is reserved for that).
  if (index) return index.get(id) ?? unknownAssignee(id, pools.gone);

  const member = (pools.members ?? []).find((m) => m.userId === id);
  if (member) return memberAssignee(member);

  const cybo = (pools.cybos ?? []).find((c) => c.id === id);
  if (cybo) return cyboAssignee(cybo);

  // A session matches by its raw agentId OR — once folded — by its cyboId, so a task
  // assigned to either the session or the cybo resolves to the one cybo identity
  // (parity with buildAssigneeIndex's dual keys above).
  const agent = (pools.agents ?? []).find((a) => a.agentId === id || a.cyboId === id);
  if (agent) return agentAssignee(agent);

  return unknownAssignee(id, pools.gone);
}
